const S3_ORIGINS = [
  "https://welltrainer-856068710566-sa-east-1-an.s3.sa-east-1.amazonaws.com",
  "https://welltrainer-856068710566-sa-east-1-an.s3.amazonaws.com",
  "https://welltrainer.s3.us-east-1.amazonaws.com",
  "https://welltrainer.s3.amazonaws.com",
]
const CDN_URL = "https://d35xiujx6az38g.cloudfront.net"

/**
 * Converte URL do S3 para CDN (CloudFront).
 * Funciona com qualquer URL, se não for do S3, retorna sem alterar.
 */
export function cdnUrl(url: string): string {
  if (!url) return url
  for (const origin of S3_ORIGINS) {
    if (url.startsWith(origin)) {
      return CDN_URL + url.slice(origin.length)
    }
  }
  return url
}

/**
 * Escolhe a melhor URL de vídeo para carregar.
 *
 * Prefere a versão 720p (~2Mbps, ~5MB) que funciona em redes ruins. Cai pra
 * versão original só se a 720p ainda não foi gerada (novo upload antes do
 * transcoding, ou backfill pendente).
 *
 * `cdnUrl()` é aplicado antes de retornar, pra cobrir buckets legado.
 */
export function bestVideoUrl(input: {
  videoUrl?: string | null
  videoUrl720p?: string | null
}): string | null {
  const chosen = input.videoUrl720p || input.videoUrl
  return chosen ? cdnUrl(chosen) : null
}

// ============================================================================
// Foto de perfil, cache-busting por versão ESTÁVEL
// ----------------------------------------------------------------------------
// A foto vive numa chave S3 fixa (`usuarios/<id>/foto-perfil.webp`); após uma
// troca a URL é idêntica, então o browser serviria a versão antiga em cache. A
// correção é um cache-buster `?v=<versao>` que muda APENAS quando a foto muda e
// fica estável entre trocas, preservando o cache normal.
//
// ATENÇÃO: o `?v=` resolve o cache do NAVEGADOR, e só ele. A distribuição do
// CloudFront usa a política `Managed-CachingOptimized`, que descarta a query
// string na chave de cache: para a CDN, `?v=1` e `?v=2` são o mesmo objeto. Foi
// isso que fez a foto recém-trocada voltar para a antiga quando a pessoa
// reabria o app. Quem tira a foto antiga da CDN é a API, que manda limpar o
// endereço a cada troca (`CloudFrontProvider`). Trocar a política seria o
// conserto direto, mas a distribuição está no plano gratuito, que não aceita
// política de cache customizada.
//
// NÃO usar Math.random()/Date.now() a cada render como `?v`: isso quebraria o
// cache em toda renderização, rebaixando cada avatar a um novo download a cada
// navegação (flicker + banda desperdiçada, péssimo em listas de alunos).
// ============================================================================

// ----------------------------------------------------------------------------
// Aviso de troca
// ----------------------------------------------------------------------------
// Trocar o `?v=` resolve o cache, mas não faz a tela redesenhar: quem já tinha
// renderizado o avatar continua com o `src` antigo na mão, e a pessoa fica
// olhando a foto velha achando que o upload não funcionou (ou que bugou).
//
// Por isso a troca AVISA. Quem desenha um avatar assina o aviso (pelo hook
// `useFotoPerfil`) e redesenha na hora, em qualquer canto do app: navegação,
// cabeçalho, conversa, lista.
// ----------------------------------------------------------------------------

let versaoGlobal = 0
const ouvintes = new Set<() => void>()

/** Assina o aviso de troca de foto. Devolve a função de cancelar. */
export function assinarFotoPerfil(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte)
  return () => ouvintes.delete(ouvinte)
}

/** Contador que muda a cada troca. É o `getSnapshot` do `useSyncExternalStore`. */
export function versaoDasFotos(): number {
  return versaoGlobal
}

function avisarQueTrocou(): void {
  versaoGlobal += 1
  for (const ouvinte of ouvintes) {
    try {
      ouvinte()
    } catch {
      // Um ouvinte quebrado não pode impedir os outros de saber da troca.
    }
  }
}

const OVERRIDE_PREFIX = "wt_avatar_" // data URL recém-cropada, por id
const VERSAO_PREFIX = "wt_avatar_v_" // localStorage: versão persistente por id

/**
 * Por quanto tempo o recorte guardado no aparelho continua valendo.
 *
 * Ele existe para a foto nova aparecer NA HORA, enquanto o servidor manda a CDN
 * esquecer a antiga (o `CloudFrontProvider` da API faz isso a cada troca, e a
 * limpeza leva alguns segundos). Passado esse tempo, a CDN já está servindo a
 * foto certa e quem manda é ela.
 *
 * Ele vive no `localStorage`, e não na sessão, porque fechar e reabrir o app
 * cabe dentro desses minutos: guardado só na sessão, ele sumia no fechamento e
 * a foto antiga voltava à tela antes de a limpeza da CDN alcançar o aparelho.
 */
const OVERRIDE_VALIDADE_MS = 15 * 60 * 1000

/** O recorte guardado neste aparelho, ou null quando não há (ou venceu). */
function lerOverride(usuarioId: string): string | null {
  try {
    if (typeof localStorage !== "undefined") {
      const cru = localStorage.getItem(OVERRIDE_PREFIX + usuarioId)
      if (cru) {
        const guardado = JSON.parse(cru) as { url?: string; em?: number }
        if (guardado?.url && typeof guardado.em === "number") {
          if (Date.now() - guardado.em <= OVERRIDE_VALIDADE_MS) return guardado.url
          /* Venceu: sai do caminho para não segurar espaço nem mascarar uma
             foto trocada em outro aparelho. */
          localStorage.removeItem(OVERRIDE_PREFIX + usuarioId)
        }
      }
    }
  } catch {
    // localStorage indisponível ou conteúdo inválido: cai na sessão
  }

  /* O que ficou guardado antes desta mudança morava na sessão. Continua sendo
     lido para quem estiver com o app aberto na hora da atualização. */
  try {
    if (typeof sessionStorage !== "undefined") {
      return sessionStorage.getItem(OVERRIDE_PREFIX + usuarioId)
    }
  } catch {
    // sessionStorage indisponível
  }
  return null
}

/** Converte uma "versão" (timestamp em ms, número ou ISO date) em inteiro. */
function normalizarVersao(versao?: string | number | null): number | null {
  if (versao === null || versao === undefined || versao === "") return null
  if (typeof versao === "number") return Number.isFinite(versao) ? Math.floor(versao) : null
  const txt = versao.trim()
  if (!txt) return null
  if (/^\d+$/.test(txt)) return Number(txt)
  const t = Date.parse(txt)
  return Number.isNaN(t) ? null : t
}

function lerVersaoLocal(usuarioId: string): number | null {
  try {
    if (typeof localStorage === "undefined") return null
    return normalizarVersao(localStorage.getItem(VERSAO_PREFIX + usuarioId))
  } catch {
    return null
  }
}

/**
 * Monta URL de foto de perfil do usuário via CDN, com cache-buster por versão.
 *
 * Resolução (maior precedência primeiro):
 *   1. Recorte guardado no aparelho (data URL, válida por poucos minutos),
 *      atualização instantânea pós-upload, sem esperar o CloudFront propagar.
 *   2. `versao` explícita (ex.: `foto_atualizada_em` do backend), cobre
 *      cross-device e troca feita por outro usuário (aluno troca → personal vê).
 *   3. Versão persistida em localStorage (`wt_avatar_v_<id>`), cobre o próprio
 *      device após reabrir o app.
 * Sem nenhuma versão, retorna a URL base (totalmente cacheável).
 */
export function fotoPerfil(usuarioId: string, versao?: string | number): string {
  const base = `${CDN_URL}/usuarios/${usuarioId}/foto-perfil.webp`
  if (!usuarioId) return base

  const override = lerOverride(usuarioId)
  if (override) return override

  const v = Math.max(normalizarVersao(versao) ?? 0, lerVersaoLocal(usuarioId) ?? 0)
  return v > 0 ? `${base}?v=${v}` : base
}

/**
 * O aparelho já sabe que esta pessoa tem foto?
 *
 * Existe porque o `user` de cada tela é uma cópia própria, lida do
 * `localStorage` quando aquela tela montou: quem trocou a foto no perfil não
 * atualiza o `foto_atualizada_em` que a navegação tem na mão, e a navegação
 * continuava desenhando as iniciais depois do upload.
 *
 * Aqui a resposta vem do que a troca deixou gravado (o recorte guardado, ou a
 * versão no `localStorage`), que é a informação mais nova que existe no
 * aparelho, venha ela de qual tela vier.
 */
export function temFotoPerfilLocal(usuarioId?: string | null): boolean {
  if (!usuarioId) return false
  if (lerOverride(usuarioId)) return true
  return (lerVersaoLocal(usuarioId) ?? 0) > 0
}

/**
 * Registra uma nova versão da foto de perfil em localStorage (cache-buster
 * persistente por usuário). Monotônico: nunca regride, um GET antigo não
 * sobrescreve um upload recente. Sem `versao`, usa o instante atual.
 *
 * Útil tanto no upload (próprio usuário) quanto para "semear" versões vindas do
 * backend (ex.: lista de alunos passa `foto_atualizada_em` de cada um, e daí
 * todos os call sites que usam `fotoPerfil(id)` já refletem a foto correta).
 */
export function bumpFotoPerfilVersao(usuarioId: string, versao?: string | number): void {
  if (!usuarioId) return
  // Sem versão válida não faz nada, evita bumpar com "agora" durante seeding
  // (ex.: foto_atualizada_em nulo no GET), o que quebraria o cache a cada load.
  // No upload, o call site passa Date.now() explicitamente.
  const nova = normalizarVersao(versao)
  if (nova === null) return
  try {
    if (typeof localStorage === "undefined") return
    const atual = lerVersaoLocal(usuarioId) ?? 0
    if (nova > atual) {
      localStorage.setItem(VERSAO_PREFIX + usuarioId, String(nova))
      avisarQueTrocou()
    }
  } catch {
    // localStorage cheio/indisponível, ignora
  }
}

/**
 * Guarda no aparelho o recorte que a pessoa acabou de escolher (data URL).
 * Todas as chamadas a `fotoPerfil(userId)` devolvem esse valor pelos próximos
 * minutos, e a versão persistente é incrementada para que outras telas e
 * reaberturas do app já usem o novo `?v=`.
 *
 * O prazo curto é o ponto: ele cobre a janela entre o envio e a hora em que a
 * CDN esquece a foto antiga (a API manda limpar a cada troca), e depois sai da
 * frente para a foto de verdade valer.
 */
export function setFotoPerfilOverride(usuarioId: string, dataUrl: string): void {
  try {
    localStorage.setItem(
      OVERRIDE_PREFIX + usuarioId,
      JSON.stringify({ url: dataUrl, em: Date.now() }),
    )
  } catch {
    /* localStorage cheio (a data URL de uma foto não é pequena): a sessão ainda
       cobre o app aberto, que é o caso mais visível. */
    try {
      sessionStorage.setItem(OVERRIDE_PREFIX + usuarioId, dataUrl)
    } catch {
      // Sem lugar para guardar: a foto nova aparece quando a CDN atualizar.
    }
  }
  bumpFotoPerfilVersao(usuarioId, Date.now())
  /* De novo, e de propósito: o `bump` só avisa quando a versão AVANÇA, e o
     override precisa avisar sempre (é ele que faz a foto nova aparecer antes
     de o CloudFront propagar). */
  avisarQueTrocou()
}

/**
 * Remove o recorte guardado no aparelho (a prévia otimista da troca).
 *
 * Chamado quando o upload falha: sem isso a data URL fica guardada e
 * `fotoPerfil()` continua devolvendo a foto nova em TODAS as telas, dando a
 * impressão de que salvou quando nada foi salvo.
 *
 * A versão em localStorage não é revertida de propósito: ela é só um
 * cache-buster monotônico, e voltar atrás poderia mascarar uma foto legítima
 * trocada em outro aparelho.
 */
export function limparFotoPerfilOverride(usuarioId: string): void {
  if (!usuarioId) return
  /* Os dois lugares: o de hoje e o da sessão, que é onde o recorte ficava antes
     e ainda pode estar num app que não recarregou. */
  try {
    localStorage.removeItem(OVERRIDE_PREFIX + usuarioId)
  } catch {
    // localStorage indisponível, ignora
  }
  try {
    sessionStorage.removeItem(OVERRIDE_PREFIX + usuarioId)
  } catch {
    // sessionStorage indisponível, ignora
  }
}

/**
 * A foto desta pessoa foi REMOVIDA no servidor.
 *
 * Duas coisas precisam acontecer no aparelho, e nenhuma delas é óbvia:
 *
 * 1. **O recorte guardado no aparelho sai.** Ele é a foto que a pessoa acabou de
 *    escolher, e enquanto estiver lá `fotoPerfil()` devolve a imagem removida
 *    em todas as telas.
 * 2. **A versão avança.** O arquivo não existe mais, mas a URL é a mesma de
 *    sempre, e o CloudFront continuaria servindo a imagem do cache. Com o `?v=`
 *    novo o endereço muda, a resposta passa a ser "não existe" e o avatar cai
 *    nas iniciais, que é o que ele mostra para quem nunca subiu foto.
 *
 * E avisa quem desenha avatar, para a remoção aparecer sem recarregar a tela.
 */
export function marcarFotoPerfilRemovida(usuarioId: string): void {
  if (!usuarioId) return
  limparFotoPerfilOverride(usuarioId)
  bumpFotoPerfilVersao(usuarioId, Date.now())
  avisarQueTrocou()
}
